// ─── Supprimer un calendrier : inventaire PUIS cascade ────────────
// La suite faisait un simple `deleteDoc` sur le document calendrier. Tout le
// reste — événements, EDT, garde, listes, budgets, dépenses, journal, contacts,
// discussions, repas… — survivait en ORPHELIN : invisible (tout se liste par
// calendrier ou par espace) et impossible à récupérer par l'utilisateur.
//
// Deux fonctions, volontairement séparées :
//   • `inventorierCalendrier()` — ce qui SERA détruit, par famille. Sert à
//     l'écrire noir sur blanc AVANT de demander confirmation : une suppression
//     irréversible qui n'annonce pas son périmètre est un piège.
//   • `supprimerCalendrierCascade()` — la destruction elle-même, par lots.
//
// Périmètre repris de MoPCal (`services/calendriers.js`), qui fait autorité : la
// même suppression doit détruire la même chose dans les deux applications.
import {
  collection, doc, getDocs, query, where, writeBatch,
} from 'firebase/firestore';
import { db, journaliser } from '@mop/core';

// Collections PLATES portant `calendrierId` (ou un autre champ de rattachement).
// `discussions`/`messages` en sont volontairement ABSENTES : depuis la
// migration du 09/08 (0.15.7.0), la messagerie vit en sous-collections
// (`calendriers/{id}/discussions/{discId}/messages/{msgId}`, cf. plus bas) et
// les anciennes collections plates sont désormais LECTURE SEULE côté règles
// (`allow write: if false`, `firestore.rules`) — un `batch.delete()` dessus
// échouerait, et ferait échouer TOUT le lot qui le porte (writeBatch est
// atomique). La source plate n'est de toute façon plus la donnée réelle.
const PAR_CHAMP = [
  { col: 'demandes',           champ: 'enfantId',      famille: 'demandes' },
  { col: 'depenses',           champ: 'calendrierId',  famille: 'finances' },
  { col: 'budgets',            champ: 'calendrierId',  famille: 'finances' },
  { col: 'recurrences_budget', champ: 'calendrierId',  famille: 'finances' },
  { col: 'import_regles',      champ: 'calendrierId',  famille: 'finances' },
  { col: 'journal',            champ: 'calendrierId',  famille: 'journal' },
  { col: 'contacts',           champ: 'calendrierId',  famille: 'contacts' },
];

// Sous-collections directes du calendrier : [collection, ...segments].
const SOUS_COLL = [
  { chemin: ['evenements', '{id}', 'jours'],            famille: 'evenements' },
  { chemin: ['evenementsRecurrents', '{id}', 'events'], famille: 'evenements' },
  { chemin: ['calendriers', '{id}', 'jours'],           famille: 'garde' },
  { chemin: ['calendriers', '{id}', 'historique'],      famille: 'historique' },
  { chemin: ['emploiDuTemps', '{id}', 'grilles'],       famille: 'edt' },
  { chemin: ['repasSemaines', '{id}', 'semaines'],      famille: 'repas' },
];

const refsDe = async (segments) => {
  try {
    const snap = await getDocs(collection(db, ...segments));
    return snap.docs.map(d => d.ref);
  } catch { return []; }
};

// Toutes les références à détruire, groupées par famille (pour l'annonce).
async function collecter(calId) {
  const parFamille = {};
  const pousser = (famille, refs) => {
    if (!refs.length) return;
    parFamille[famille] = [...(parFamille[famille] || []), ...refs];
  };

  for (const s of SOUS_COLL) {
    pousser(s.famille, await refsDe(s.chemin.map(x => (x === '{id}' ? calId : x))));
  }
  // Le doc emploiDuTemps/{calId} lui-même (les grilles sont dessous).
  pousser('edt', [doc(db, 'emploiDuTemps', calId)]);

  // Messagerie : sous-collections `calendriers/{calId}/discussions/{discId}/
  // messages/{msgId}` (migration 0.15.7.0, cf. commentaire sur PAR_CHAMP).
  // Les messages d'abord, la discussion (leur conteneur) ensuite — même ordre
  // de précaution que `supprimerDiscussion` (Core) : un lot de messages qui
  // échoue laisse la discussion visible et l'opération se relance, plutôt que
  // des messages orphelins et irrécupérables.
  try {
    const discs = await getDocs(collection(db, 'calendriers', calId, 'discussions'));
    for (const d of discs.docs) {
      pousser('messagerie', await refsDe(['calendriers', calId, 'discussions', d.id, 'messages']));
      pousser('messagerie', [d.ref]);
    }
  } catch { /* collection absente ou illisible */ }

  // Listes : le document ET ses items.
  try {
    const listes = await getDocs(query(collection(db, 'listes'), where('calendrierId', '==', calId)));
    for (const l of listes.docs) {
      pousser('listes', await refsDe(['listes', l.id, 'items']));
      pousser('listes', [l.ref]);
    }
  } catch { /* collection absente ou illisible */ }

  for (const p of PAR_CHAMP) {
    try {
      const snap = await getDocs(query(collection(db, p.col), where(p.champ, '==', calId)));
      pousser(p.famille, snap.docs.map(d => d.ref));
    } catch { /* idem */ }
  }

  // Repas : config (doc = calId) + menus et leurs exceptions.
  pousser('repas', [doc(db, 'repasConfig', calId)]);
  try {
    const menus = await getDocs(collection(db, 'repasMenus', calId, 'menus'));
    for (const m of menus.docs) {
      pousser('repas', await refsDe(['repasMenus', calId, 'menus', m.id, 'exceptions']));
      pousser('repas', [m.ref]);
    }
  } catch { /* idem */ }

  return parFamille;
}

// → { total, parFamille: { finances: 12, evenements: 40, … } }
// Les familles VIDES ne sont pas listées : on annonce ce qui existe vraiment.
export async function inventorierCalendrier(calId) {
  if (!calId) return { total: 0, parFamille: {} };
  const refs = await collecter(calId);
  const parFamille = {};
  let total = 0;
  Object.entries(refs).forEach(([f, l]) => {
    // `historique` et les documents « conteneurs » toujours présents ne sont pas
    // des données de l'utilisateur : on ne les compte pas dans l'annonce.
    if (f === 'historique') return;
    if (l.length) { parFamille[f] = l.length; total += l.length; }
  });
  return { total, parFamille };
}

export async function supprimerCalendrierCascade(calId, auteur) {
  if (!calId) return;
  const parFamille = await collecter(calId);
  const refs = Object.values(parFamille).flat();

  // Trace AVANT destruction : l'historique du calendrier part avec lui, mais
  // l'entrée aura existé le temps que d'éventuels lecteurs la voient. C'est
  // surtout le principe qui compte — une suppression doit se journaliser.
  journaliser(calId, {
    type: 'calendrier', action: 'suppression',
    titre: auteur?.nomCalendrier || '',
    detail: Object.entries(parFamille).map(([f, l]) => `${f}:${l.length}`).join(' '),
    par: auteur?.uid || null, parNom: auteur?.nom || '',
  });

  refs.push(doc(db, 'calendriers', calId));   // les métadonnées en dernier
  const LOT = 450;                            // writeBatch plafonne à 500
  for (let i = 0; i < refs.length; i += LOT) {
    const batch = writeBatch(db);
    refs.slice(i, i + LOT).forEach(r => batch.delete(r));
    await batch.commit();
  }
}
